import {
  useEffect,
  useEffectEvent,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from "react";

const DRAG_THRESHOLD = 4;
const KEY_PAN_FRACTION = 0.1;
const WHEEL_ZOOM_RATE = 0.01;
// A mouse wheel reports far bigger steps than a pinch does
const MAX_WHEEL_ZOOM_DELTA = 30;

interface Options {
  isEnabled: boolean;
  // How much one key press zooms
  zoomStep: number;
  // A fraction of the element's width; positive moves towards what is on the right
  onPan: (fraction: number) => void;
  // A factor below 1 zooms in; the anchor is where the pointer is, as a fraction of the width
  onZoom: (factor: number, anchor: number) => void;
}

interface Drag {
  pointerId: number;
  x: number;
  isPanning: boolean;
}

// Drag, sideways scroll and arrow keys pan; pinch, Ctrl or ⌘ with scroll, and + and - zoom
export const usePanZoom = <T extends HTMLElement>({
  isEnabled,
  zoomStep,
  onPan,
  onZoom,
}: Options) => {
  const ref = useRef<T>(null);
  const dragRef = useRef<Drag | null>(null);
  const didPanRef = useRef(false);

  const onWheel = useEffectEvent((event: WheelEvent) => {
    const element = ref.current;
    if (!element) return;

    const { left, width } = element.getBoundingClientRect();

    if (event.ctrlKey || event.metaKey) {
      const delta = Math.max(
        -MAX_WHEEL_ZOOM_DELTA,
        Math.min(MAX_WHEEL_ZOOM_DELTA, event.deltaY),
      );

      event.preventDefault();
      onZoom(Math.exp(delta * WHEEL_ZOOM_RATE), (event.clientX - left) / width);
      return;
    }

    // Vertical scrolling stays with the page
    const isSideways =
      event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
    if (!isSideways) return;

    event.preventDefault();
    // Shift with a mouse wheel arrives as deltaY in some browsers
    onPan((event.deltaX || event.deltaY) / width);
  });

  useEffect(() => {
    const element = ref.current;
    if (!element || !isEnabled) return;

    // React listens passively, which cannot stop the browser's own zoom and scroll
    const listener = (event: WheelEvent) => onWheel(event);

    element.addEventListener("wheel", listener, { passive: false });
    return () => element.removeEventListener("wheel", listener);
  }, [isEnabled]);

  const endDrag = () => {
    dragRef.current = null;
  };

  return {
    ref,
    onPointerDown: (event: PointerEvent<T>) => {
      didPanRef.current = false;
      if (!isEnabled || event.button !== 0) return;

      dragRef.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        isPanning: false,
      };
    },
    onPointerMove: (event: PointerEvent<T>) => {
      const drag = dragRef.current;
      if (drag?.pointerId !== event.pointerId) return;

      // The button was released somewhere this element never heard about
      if (event.buttons === 0) {
        endDrag();
        return;
      }

      const distance = event.clientX - drag.x;
      if (!drag.isPanning && Math.abs(distance) < DRAG_THRESHOLD) return;

      if (!drag.isPanning) {
        drag.isPanning = true;
        didPanRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      drag.x = event.clientX;
      onPan(-distance / event.currentTarget.getBoundingClientRect().width);
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    // Letting go after a drag must not also press what is under the pointer
    onClickCapture: (event: MouseEvent<T>) => {
      if (!didPanRef.current) return;

      didPanRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    onKeyDown: (event: KeyboardEvent<T>) => {
      const hasModifier = event.ctrlKey || event.metaKey || event.altKey;
      if (!isEnabled || hasModifier) return;

      const zoomIn = () => onZoom(1 / zoomStep, 0.5);
      const actions: Record<string, (() => void) | undefined> = {
        ArrowLeft: () => onPan(-KEY_PAN_FRACTION),
        ArrowRight: () => onPan(KEY_PAN_FRACTION),
        "+": zoomIn,
        "=": zoomIn,
        "-": () => onZoom(zoomStep, 0.5),
      };
      const action = actions[event.key];
      if (!action) return;

      event.preventDefault();
      action();
    },
  };
};
